/**
 * The prescription protocols, as a catalogue (`D102`, `D118`).
 *
 * ── WHY A CATALOGUE AND NOT A CONSTANT ──────────────────────────────────────
 * Sets, rep range, rest, progression rule, deload cadence and split transition
 * are not one rule the engine hard-codes. Three actors choose among them
 * (`D102`): the system applies the default (`D103`), a human trainer picks
 * another through the agent, and the agent may PROPOSE one — citing the record
 * — which takes effect only once the person accepts. None of that is
 * expressible against a constant, and a source of truth a human can point at
 * is the whole reason the owner asked for it.
 *
 * ── WHY HERE AND NOT IN `@repo/training` ────────────────────────────────────
 * `apps/mobile` reads `@repo/contract` and never `packages/modules/*`, and the
 * app has to show a protocol's NAME beside the numbers the engine applied.
 * `D118` settles it: the definitions live here, following the permission
 * catalogue (`authz/permission.ts`); `D102`'s "entities of the training
 * module" names the plan's `protocol_id` column and the engine that applies
 * these numbers, not the directory they sit in.
 *
 * ── WHY THERE IS NO ZOD SCHEMA HERE ─────────────────────────────────────────
 * The same reason `permission.ts:15-20` gives. Every other file in this
 * package describes something that crosses the wire and is therefore parsed.
 * The catalogue crosses nothing — it is compiled into both sides. What travels
 * is a `protocolId` string, validated by `isProtocolId`.
 *
 * ── EVIDENCE IS NAMED, NEVER IMPORTED ───────────────────────────────────────
 * `evidenceIds` are identifiers of evidence records in `@repo/agents`
 * (`D102`). This package cannot import that module and must not try: the ids
 * are strings here, and a test in the composition root — the one place
 * allowed to see both `@repo/training` and `@repo/agents` — joins them against
 * the published skill manifest. That test is the only thing standing between a
 * protocol and a citation that names nothing.
 */
/**
 * `const Id` is the whole point, exactly as `definePermission`: it keeps the
 * id a literal so the catalogue can produce a union rather than `string`.
 */
export function defineProtocol(definition) {
    return Object.freeze({
        ...definition,
        evidenceIds: Object.freeze([...definition.evidenceIds]),
        loadStepKg: Object.freeze({ ...definition.loadStepKg }),
        repRange: Object.freeze({ ...definition.repRange }),
        restSeconds: Object.freeze({ ...definition.restSeconds }),
        rir: Object.freeze({ ...definition.rir }),
        setsPerExercise: Object.freeze({ ...definition.setsPerExercise }),
    });
}
/**
 * The default (`D103`): first workout 2 × 10–12, three sets from week 3, two
 * minutes after a compound and ninety seconds after an isolation exercise.
 *
 * The split transition sits at 26 weeks because that is the reading with a
 * cited source — ACSM's ~6 months — and `D102` says the default is the cited
 * one where the record disagrees with itself. The impatient reading is the
 * next protocol, not a knob on this one.
 */
const BEGINNER_FULL_BODY = defineProtocol({
    deloadEveryWeeks: 4,
    deloadVolumeFactor: 0.5,
    evidenceIds: [
        "acsm-2026-position-stand",
        "ramos-campo-2024-split",
        "krieger-2010-sets",
        "schoenfeld-2021-repetition-continuum",
        "schoenfeld-2016-rest-intervals",
        "robinson-2024-proximity",
        "chaves-2024-progression",
    ],
    goal: "any",
    id: "beginner-full-body",
    isDefault: true,
    isolationFromWeek: 5,
    level: "beginner",
    loadStepKg: { lower: 5, upper: 2.5 },
    nameKey: "protocol.beginner-full-body.name",
    progression: "double",
    repRange: { max: 12, min: 10 },
    restSeconds: { compound: 120, isolation: 90 },
    rir: { max: 3, min: 2 },
    setsPerExercise: { from3: 3, weeks1to2: 2 },
    split: "by_days",
    splitTransitionAfterWeeks: 26,
});
/**
 * The same programme, moved off full body at twelve weeks.
 *
 * `D102`'s aggressive variant of the split transition: the record carries both
 * ~6 months and 12 weeks, so both exist and the person or their trainer picks.
 * Nothing else about the protocol changes — that is the point of it being a
 * variant rather than a second opinion about training.
 */
const BEGINNER_FULL_BODY_FAST_SPLIT = defineProtocol({
    deloadEveryWeeks: 4,
    deloadVolumeFactor: 0.5,
    evidenceIds: [
        "ramos-campo-2024-split",
        "acsm-2026-position-stand",
        "krieger-2010-sets",
        "schoenfeld-2021-repetition-continuum",
        "schoenfeld-2016-rest-intervals",
        "robinson-2024-proximity",
        "chaves-2024-progression",
    ],
    goal: "any",
    id: "beginner-full-body-fast-split",
    isDefault: false,
    isolationFromWeek: 5,
    level: "beginner",
    loadStepKg: { lower: 5, upper: 2.5 },
    nameKey: "protocol.beginner-full-body-fast-split.name",
    progression: "double",
    repRange: { max: 12, min: 10 },
    restSeconds: { compound: 120, isolation: 90 },
    rir: { max: 3, min: 2 },
    setsPerExercise: { from3: 3, weeks1to2: 2 },
    split: "by_days",
    splitTransitionAfterWeeks: 12,
});
/**
 * The deload stated as a 3:1 block — three loading weeks, then the fourth
 * unloads.
 *
 * `D102`'s second contradiction in the record: "deload every fourth week" and
 * "3:1" are two ways the sources say the same cadence, and the decision is
 * that both become variants rather than one being silently chosen. The numbers
 * are therefore identical to the default; what differs is the NAME the trainer
 * chooses and the record it cites, which is exactly what a catalogue is for.
 */
const BEGINNER_DELOAD_3_1 = defineProtocol({
    deloadEveryWeeks: 4,
    deloadVolumeFactor: 0.5,
    evidenceIds: [
        "acsm-2026-position-stand",
        "krieger-2010-sets",
        "schoenfeld-2021-repetition-continuum",
        "schoenfeld-2016-rest-intervals",
        "robinson-2024-proximity",
        "chaves-2024-progression",
    ],
    goal: "any",
    id: "beginner-deload-3-1",
    isDefault: false,
    isolationFromWeek: 5,
    level: "beginner",
    loadStepKg: { lower: 5, upper: 2.5 },
    nameKey: "protocol.beginner-deload-3-1.name",
    progression: "double",
    repRange: { max: 12, min: 10 },
    restSeconds: { compound: 120, isolation: 90 },
    rir: { max: 3, min: 2 },
    setsPerExercise: { from3: 3, weeks1to2: 2 },
    split: "by_days",
    splitTransitionAfterWeeks: 26,
});
/**
 * Intermediate, hypertrophy: upper/lower, 6–15 reps varied by block.
 *
 * `splitTransitionAfterWeeks` is `null` because upper/lower IS where the
 * beginner protocols were heading; the next step is undulating periodisation,
 * which is a different axis and not a split.
 */
const INTERMEDIATE_HYPERTROPHY = defineProtocol({
    deloadEveryWeeks: 4,
    deloadVolumeFactor: 0.5,
    evidenceIds: [
        "pelland-2024-volume",
        "schoenfeld-2021-repetition-continuum",
        "schoenfeld-2016-rest-intervals",
        "robinson-2024-proximity",
        "chaves-2024-progression",
        "acsm-2026-position-stand",
    ],
    goal: "hypertrophy",
    id: "intermediate-hypertrophy",
    isDefault: false,
    isolationFromWeek: 1,
    level: "intermediate",
    loadStepKg: { lower: 5, upper: 2.5 },
    nameKey: "protocol.intermediate-hypertrophy.name",
    progression: "double",
    repRange: { max: 15, min: 6 },
    restSeconds: { compound: 150, isolation: 90 },
    rir: { max: 3, min: 1 },
    setsPerExercise: { from3: 3, weeks1to2: 3 },
    split: "upper_lower",
    splitTransitionAfterWeeks: null,
});
/**
 * Intermediate, strength: 3–6 reps at 80–90% 1RM, four minutes between
 * compound sets, and linear progression rather than double — at three reps a
 * range is too narrow for the top of it to mean anything.
 */
const INTERMEDIATE_STRENGTH = defineProtocol({
    deloadEveryWeeks: 4,
    deloadVolumeFactor: 0.5,
    evidenceIds: [
        "acsm-2026-position-stand",
        "schoenfeld-2021-repetition-continuum",
        "schoenfeld-2016-rest-intervals",
        "robinson-2024-proximity",
        "chaves-2024-progression",
    ],
    goal: "strength",
    id: "intermediate-strength",
    isDefault: false,
    isolationFromWeek: 1,
    level: "intermediate",
    loadStepKg: { lower: 5, upper: 2.5 },
    nameKey: "protocol.intermediate-strength.name",
    progression: "linear",
    repRange: { max: 6, min: 3 },
    restSeconds: { compound: 240, isolation: 120 },
    rir: { max: 3, min: 1 },
    setsPerExercise: { from3: 3, weeks1to2: 3 },
    split: "upper_lower",
    splitTransitionAfterWeeks: null,
});
/**
 * Every protocol that exists, in one frozen list.
 *
 * Three things read it, exactly as `PERMISSION_CATALOGUE`: the engine to
 * apply the numbers, the app to show the name, and `isProtocolId` to refuse a
 * `protocol_id` outside it — the foreign key a seeded-in-code catalogue gives
 * up. Do not add a protocol without a decision: `D102` fixed which readings of
 * the record become variants, and an extra one here is an unsourced
 * prescription the agent may then cite.
 */
export const PROTOCOL_CATALOGUE = Object.freeze([
    BEGINNER_FULL_BODY,
    BEGINNER_FULL_BODY_FAST_SPLIT,
    BEGINNER_DELOAD_3_1,
    INTERMEDIATE_HYPERTROPHY,
    INTERMEDIATE_STRENGTH,
]);
/** The ids alone, for a membership test that does not need the parameters. */
export const PROTOCOL_IDS = Object.freeze(PROTOCOL_CATALOGUE.map((protocol) => protocol.id));
/**
 * The protocol the system applies when nobody chose (`D103`).
 *
 * Resolved from the catalogue rather than aliased to the constant, so that
 * moving `isDefault` moves this too. `protocols.test.ts` asserts exactly one
 * definition claims it.
 */
export const DEFAULT_PROTOCOL = PROTOCOL_CATALOGUE.find((protocol) => protocol.isDefault) ??
    BEGINNER_FULL_BODY;
/** The definition for an id, or `undefined` — for rendering, and for the engine. */
export function findProtocol(id) {
    return PROTOCOL_CATALOGUE.find((protocol) => protocol.id === id);
}
/** Does this id name a protocol at all? The check a stored `protocol_id` gets. */
export function isProtocolId(value) {
    return PROTOCOL_IDS.includes(value);
}
/**
 * The absolute limits, which are NOT per protocol (`D102`).
 *
 * They stay in code and no protocol crosses them, because they are the part
 * of AGENT-RULES that survives whoever is choosing: a trainer picking a
 * protocol, and an agent proposing one, both stay inside these. A per-protocol
 * ceiling would be a ceiling the chooser can raise, which is not a ceiling.
 */
export const CEILINGS = Object.freeze({
    /** A beginner never trains to failure — RIR never drops below this. */
    beginnerMaxRirFloor: 2,
    /** Week-over-week load increase, as a percentage. Never more than +10%. */
    maxWeeklyLoadIncreasePct: 10,
    /** Junk volume beyond this in one session for one muscle (`pelland-2024-volume`). */
    maxSetsPerSessionPerMuscle: 10,
});
/** The floors, which like the ceilings are not per protocol (`D102`). */
export const FLOORS = Object.freeze({
    /** Minimum effective dose, in FRACTIONAL weekly sets per muscle (`pelland-2024-volume`). */
    minEffectiveWeeklySetsPerMuscle: 4,
});
/**
 * Where a muscle's weekly volume should land once the ramp is over (`D212`),
 * in the same FRACTIONAL sets as the floor: 1 per set for a target muscle,
 * 0.5 for a synergist (`D182`).
 *
 * `pelland-2024-volume` puts the minimum effective dose at about 4 (the
 * floor above), estimates its marginal slope at the trials' average of 12.25,
 * and finds each further detectable increment costing more than three times
 * the first. So the band tops out at 12 — where the record measured — and
 * starts at 8, twice the dose: the lower edge is the engine's choice, not a
 * finding, and `D212` says so. A PRIORITY muscle (`D213`) aims one band
 * higher, still well inside the ~25 sets the trials reached.
 *
 * The generator reads it at the protocol's steady set count (`from3`): the
 * first two weeks are a ramp (`D103`), and a band met in week 1 would be a
 * band overshot from week 3.
 */
export const WEEKLY_SET_TARGETS = Object.freeze({
    normal: Object.freeze({ max: 12, min: 8 }),
    priority: Object.freeze({ max: 16, min: 12 }),
});
/**
 * What an exercise does as a movement (`D212`) — the catalogue's
 * `catalog_exercise.pattern`. The first nine build a week; the rest are the
 * single-joint work around them. Held equal to the training module's list by
 * `contract-vocabulary.test.ts`.
 */
export const MOVEMENT_PATTERNS = Object.freeze([
    "horizontal_push",
    "vertical_push",
    "horizontal_pull",
    "vertical_pull",
    "squat",
    "hinge",
    "lunge",
    "trunk",
    "carry",
    "chest_fly",
    "shoulder_raise",
    "rear_delt",
    "elbow_flexion",
    "elbow_extension",
    "wrist",
    "shrug",
    "knee_extension",
    "knee_flexion",
    "hip_extension",
    "hip_abduction",
    "hip_adduction",
    "calf_raise",
    "neck",
]);
/**
 * The week shapes the generator builds (`D213`, amending `D158`).
 * `ppl_arms` is push, pull, legs and an arms-and-shoulders day; `custom` is
 * a list of days, each with its label and its patterns.
 */
export const PLAN_SPLITS = Object.freeze([
    "full_body",
    "upper_lower",
    "ppl",
    "ppl_arms",
    "custom",
]);
/** The fewest days each split needs to train every movement once a week. */
export const SPLIT_MIN_DAYS = Object.freeze({
    custom: 2,
    full_body: 2,
    ppl: 3,
    ppl_arms: 4,
    upper_lower: 2,
});
/**
 * The twelve training muscles by their English seed names, lowercased — what
 * `priorityMuscles` names (`D213`). Names rather than ids because the trainer
 * speaks in muscles and the ids are catalogue data; the engine resolves them
 * against the vocabulary it already reads.
 */
export const PRIORITY_MUSCLES = Object.freeze([
    "abs",
    "biceps",
    "calves",
    "chest",
    "forearms",
    "glutes",
    "hamstrings",
    "lats",
    "quads",
    "shoulders",
    "traps",
    "triceps",
]);
/** More than three priorities is no priority (`D213`). */
export const MAX_PRIORITY_MUSCLES = 3;
//# sourceMappingURL=protocols.js.map